import { useState, useEffect } from 'react';
import firebase from '../config/firebase.config';

const database = firebase.firestore();

/**
 * Custom hook to fetch and cache a team's attendance/alert override settings
 * (alertConfig + workingSchedule) from teamDatabase/{teamId}. Mirrors
 * useAlertConfig/useWorkingSchedule's cache-first + onSnapshot pattern, but
 * team-scoped and read together off a single listener since both fields
 * live on the same document — even though they're edited on two different
 * Settings pages (workingSchedule + timeZone in Team Settings, alertConfig
 * in Alerts & Notifications), reading them together here costs nothing
 * extra and lets consumers like the dashboard use both at once.
 *
 * Unlike the company-level hooks, there is no fallback default here: these
 * fields are either configured for this team or they aren't. `isConfigured`
 * reflects whether the team has ever saved this section — mirrors the
 * `'focusApps' in data` override-detection idiom already used for
 * per-team productivity apps.
 *
 * @param {string} [teamId]
 * @returns {{
 *   alertConfig: object|null,
 *   workingSchedule: object|null,
 *   timeZone: string|null,
 *   isConfigured: boolean,
 *   loading: boolean,
 * }}
 */
export function useTeamAttendanceSettings(teamId) {
  const [state, setState] = useState({
    alertConfig: null,
    workingSchedule: null,
    timeZone: null,
    isConfigured: false,
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!teamId) {
      setState({
        alertConfig: null,
        workingSchedule: null,
        timeZone: null,
        isConfigured: false,
      });
      setLoading(false);
      return;
    }

    const cacheKey = `teamAttendanceSettings_${teamId}`;
    const cachedValue = localStorage.getItem(cacheKey);

    if (cachedValue !== null) {
      try {
        setState(JSON.parse(cachedValue));
        setLoading(false);
      } catch {
        // Ignore a malformed cache entry - the snapshot below will replace it.
      }
    }

    const teamRef = database.collection('teamDatabase').doc(teamId);

    const unsubscribe = teamRef.onSnapshot(
      (doc) => {
        const data = doc.exists ? doc.data() : {};
        const resolved = {
          alertConfig: data.alertConfig ?? null,
          workingSchedule: data.workingSchedule ?? null,
          timeZone: data.timeZone ?? null,
          isConfigured: 'alertConfig' in data && 'workingSchedule' in data,
        };

        // The whole teamDatabase doc re-fires this listener whenever ANY
        // sibling field (timeZone, focusApps, ...) is saved. Bail out to the
        // same object when nothing we care about actually changed, so an
        // in-progress unsaved edit elsewhere on the settings page isn't
        // clobbered by an unrelated save.
        setState((prev) =>
          JSON.stringify(prev) === JSON.stringify(resolved) ? prev : resolved
        );
        localStorage.setItem(cacheKey, JSON.stringify(resolved));
        setLoading(false);
      },
      (error) => {
        console.error('Error fetching team attendance settings:', error);
        if (cachedValue === null) {
          setState({
            alertConfig: null,
            workingSchedule: null,
            timeZone: null,
            isConfigured: false,
          });
        }
        setLoading(false);
      }
    );

    return () => {
      unsubscribe();
    };
  }, [teamId]);

  return { ...state, loading };
}
